"use client";

import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Ban, Loader2, Plus, PhoneOff } from "lucide-react";

import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";
import { showToast } from "@/app/_module/utils/show-toast";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/app/_module/components/ui/card";
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import { Badge } from "@/app/_module/components/ui/badge";

interface BlockedNumber {
  id: string;
  phone: string;
  phoneDigits: string;
  leadId?: string | null;
  reason?: string | null;
  source: string;
  createdAt: string;
}

const formatPhone = (raw: string): string => {
  const d = (raw || "").replace(/[^0-9]/g, "").slice(-10);
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

/** Digits of a US number, country code dropped, capped at 10. */
const phoneDigitsOf = (raw: string): string => {
  let d = (raw || "").replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("1")) d = d.slice(1); // pasted +1…
  return d.slice(0, 10);
};

/**
 * Live input mask — the field can only ever hold "(555) 123-4567" shaped text.
 * Non-digits are dropped as they're typed, so no free-text junk reaches the API
 * and the value always normalizes to the same key the backend matches on.
 */
const maskPhoneInput = (raw: string): string => {
  const d = phoneDigitsOf(raw);
  if (!d) return "";
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

/**
 * Settings -> Blocked Numbers.
 *
 * The undo surface for inbound blocking. Rows land here either from an explicit
 * add below, or automatically when an agent moves a lead to "Do Not Call" and
 * leaves the "also block" box checked. Blocks are per-agent — nothing here
 * affects anyone else on the team.
 */
export const BlockedNumbers: React.FC = () => {
  const queryClient = useQueryClient();
  const [newPhone, setNewPhone] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["blocked-numbers"],
    queryFn: async () => {
      const result = await handleApiCall(async () =>
        api.get({ url: getApiUrl(endpoint.blockedNumbers.list), auth: true }),
      );
      const payload = (result as any)?.data ?? result;
      return (payload?.data ?? payload ?? []) as BlockedNumber[];
    },
    staleTime: 30_000,
  });

  const blocked = Array.isArray(data) ? data : [];

  const { mutate: blockNumber, isPending: isBlocking } = useMutation({
    // NOTE: this api helper takes `body`, not `data` — passing `data` sends an
    // EMPTY body, the server sees phone=undefined and silently stores nothing.
    mutationFn: async (phone: string) =>
      handleApiCall(async () =>
        api.post({
          url: getApiUrl(endpoint.blockedNumbers.block),
          body: { phone },
          auth: true,
        }),
      ),
    onSuccess: (result: any) => {
      // The endpoint answers 200 with success:false when the number couldn't be
      // parsed — surface that instead of claiming it worked.
      const payload = result?.data ?? result;
      if (payload && payload.success === false) {
        showToast(payload.message || "Could not block that number", "error");
        return;
      }
      setNewPhone("");
      queryClient.invalidateQueries({ queryKey: ["blocked-numbers"] });
      showToast("Number blocked", "success");
    },
    onError: () => showToast("Could not block that number", "error"),
  });

  const { mutate: unblockNumber } = useMutation({
    mutationFn: async (id: string) =>
      handleApiCall(async () =>
        api.delete({ url: getApiUrl(endpoint.blockedNumbers.unblock(id)), auth: true }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["blocked-numbers"] });
      showToast("Number unblocked", "success");
    },
    onError: () => showToast("Could not unblock that number", "error"),
  });

  // Exactly 10 digits — a partial number would normalize to something the
  // inbound webhook could never match.
  const digitCount = phoneDigitsOf(newPhone).length;
  const canSubmit = digitCount === 10 && !isBlocking;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Ban className="h-5 w-5 text-amber-600" />
          Blocked Numbers
        </CardTitle>
        <CardDescription>
          Calls from these numbers won&apos;t ring, and their texts won&apos;t reach you.
          Only you are affected — other agents can still hear from them.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-5">
        <div className="space-y-1.5">
        <div className="flex gap-2">
          <Input
            placeholder="(555) 123-4567"
            value={newPhone}
            inputMode="tel"
            autoComplete="tel"
            maxLength={14}
            onChange={(e) => setNewPhone(maskPhoneInput(e.target.value))}
            onKeyDown={(e) => {
              if (e.key === "Enter" && canSubmit) blockNumber(newPhone);
            }}
            className="max-w-xs tabular-nums"
          />
          <Button disabled={!canSubmit} onClick={() => blockNumber(newPhone)}>
            {isBlocking ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            <span className="ml-1.5">Block</span>
          </Button>
        </div>
        {digitCount > 0 && digitCount < 10 && (
          <p className="text-xs text-muted-foreground">
            Enter all 10 digits ({digitCount}/10).
          </p>
        )}
        </div>

        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading blocked numbers…
          </div>
        ) : blocked.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
            <PhoneOff className="h-8 w-8 text-muted-foreground/50" />
            <p className="text-sm font-medium">No blocked numbers</p>
            <p className="text-xs text-muted-foreground max-w-sm">
              Add one above, or move a lead to &quot;Do Not Call&quot; and check
              &quot;also block their calls and texts&quot;.
            </p>
          </div>
        ) : (
          <div className="divide-y rounded-md border">
            {blocked.map((b) => (
              <div key={b.id} className="flex items-center justify-between gap-3 p-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium tabular-nums">
                      {formatPhone(b.phone)}
                    </span>
                    {b.source === "dnc_move" && (
                      <Badge variant="secondary" className="text-xs">
                        Do Not Call
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Blocked{" "}
                    {new Date(b.createdAt).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                    {b.reason ? ` · ${b.reason}` : ""}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => unblockNumber(b.id)}
                  className="flex-shrink-0"
                >
                  Unblock
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BlockedNumbers;
